import Button from '@ds/components/actions/Button';
import CircularProgress from '@ds/components/feedback/CircularProgress';
import Typography from '@ds/components/typography/Typography';
import clsx from 'clsx';
import React from 'react';

import styles from './Loader.module.css';

export type LoaderStatus = 'loading' | 'error' | 'timedOut';
/** What renders in the ring's center — the eNVD wordmark, or a caller-supplied `icon`. */
export type LoaderVariant = 'text' | 'icon';

const DEFAULT_LOADING_ARIA_LABEL = 'Loading';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong.';
const DEFAULT_TIMED_OUT_MESSAGE = 'This is taking longer than expected.';

interface LoaderCommonProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
    /**
     * Current state. `'loading'` renders a spinning ring; `'error'`/`'timedOut'` render a solid
     * status ring (danger/warning) and, when `onRetry` is set, a Retry button.
     * @default 'loading'
     */
    status?: LoaderStatus;
    /**
     * Message shown under the ring while `status` is `'loading'`. Describe what's loading (e.g.
     * "Loading your declaration…") rather than a generic placeholder. Omit to render just the ring,
     * with no text underneath — the ring still gets an accessible name via a generic fallback.
     */
    message?: string;
    /** Message shown under the ring when `status` is `'error'`. */
    errorMessage?: string;
    /** Message shown under the ring when `status` is `'timedOut'`. */
    timedOutMessage?: string;
    /** Called when the Retry button is clicked. Omit to hide the button entirely. */
    onRetry?: () => void;
    /**
     * Fills the viewport height (`min-height: 100dvh`) and sits 80px from the top of the page
     * rather than vertically centred (`--space-20`, halving to 40px below 1200px). Set `false` when
     * Loader replaces content confined to a panel/modal rather than a full route.
     * @default true
     */
    fillViewport?: boolean;
    className?: string;
}

/**
 * The ring's center renders the eNVD wordmark **or** a caller-supplied `icon`, never both —
 * `variant="icon"` requires `icon`, mirroring `CircularProgress`'s own mutually-exclusive center
 * content.
 */
type LoaderCenterProps =
    | {
          /** @default 'text' */
          variant?: 'text';
          icon?: undefined;
      }
    | {
          variant: 'icon';
          /**
           * The icon shown in the ring's center instead of the wordmark. Pass it bare (e.g.
           * `<TagSearchIcon />`) — `CircularProgress`'s center slot sizes and colours it
           * automatically, matching the wordmark's treatment. Use for a loading state tied to a
           * specific action rather than a generic page load (Figma node 11336:21013 — a PIC/tag
           * search).
           */
          icon: React.ReactNode;
      };

export type LoaderProps = LoaderCommonProps & LoaderCenterProps;

/**
 * Loading state (Figma *Loading Screen*, node 11336:21154) — a large indeterminate
 * `CircularProgress` ring with the eNVD wordmark centered inside it by default, an optional status
 * message below, and an optional Retry action for the error/timedOut states.
 *
 * Used both full-page (`fillViewport` default `true`, `min-height: 100dvh`) and inline, confined to
 * a table cell / dropdown / widget (`fillViewport={false}`, typically with no `message` — just the
 * ring) — the same eNVD-wordmark ring at every loading spot in the app, rather than a bare
 * `CircularProgress` in some places and this in others. `variant="icon"` swaps the wordmark for a
 * caller-supplied `icon`, for the scoped, action-specific loading states Figma also draws (e.g.
 * node 11336:21013's PIC/tag search) — the two are mutually exclusive, never both at once.
 *
 * @example
 * <Loader message="Loading your declaration…" />
 *
 * @example
 * // No message — just the ring
 * <Loader />
 *
 * @example
 * <Loader
 *     status={error ? 'error' : 'loading'}
 *     message="Loading templates…"
 *     onRetry={retry}
 * />
 *
 * @example
 * // Confined to a table cell, dropdown, or widget — not the full viewport
 * <Loader fillViewport={false} />
 *
 * @example
 * // A custom center icon instead of the eNVD wordmark, for an action-specific load
 * <Loader variant="icon" icon={<TagSearchIcon />} message="Searching for the PIC…" />
 */
const Loader: React.FC<LoaderProps> = ({
    status = 'loading',
    message,
    errorMessage = DEFAULT_ERROR_MESSAGE,
    timedOutMessage = DEFAULT_TIMED_OUT_MESSAGE,
    onRetry,
    fillViewport = true,
    variant = 'text',
    icon,
    className,
    ...rest
}) => {
    const displayMessage = status === 'error' ? errorMessage : status === 'timedOut' ? timedOutMessage : message;
    const ariaLabel = displayMessage ?? DEFAULT_LOADING_ARIA_LABEL;

    const brandMark = (
        <Typography
            variant="heading-1"
            color="secondary-2"
            component="span"
        >
            eNVD
        </Typography>
    );
    const centerIcon = variant === 'icon' ? icon : brandMark;

    return (
        <div
            data-status={status}
            data-fill-viewport={fillViewport || undefined}
            className={clsx(styles.root, className)}
            {...rest}
        >
            {status === 'loading' ? (
                <CircularProgress
                    indeterminate
                    size="xxl"
                    icon={centerIcon}
                    aria-label={ariaLabel}
                />
            ) : (
                <CircularProgress
                    status={status === 'error' ? 'danger' : 'warning'}
                    size="xxl"
                    icon={centerIcon}
                    aria-label={ariaLabel}
                />
            )}
            {displayMessage && (
                <Typography
                    variant="body-1"
                    color="placeholder"
                    component="p"
                    role="status"
                    aria-live="polite"
                    className={styles.message}
                >
                    {displayMessage}
                </Typography>
            )}
            {status !== 'loading' && onRetry && (
                <Button
                    variant="secondary"
                    danger
                    size="sm"
                    onClick={onRetry}
                >
                    Retry
                </Button>
            )}
        </div>
    );
};

Loader.displayName = 'Loader';

export default Loader;
